/** What got set, what did not, and where to go next.
 *
 *  The distinction that matters here is **skipped vs failed**. Both leave the client without that
 *  piece, but one was a decision and the other is unfinished work — a green summary over a failed
 *  write is worse than no summary at all, so a step left in `dirty` with an error reads as
 *  "ההגדרה נכשלה" and keeps its jump-back link. */

import { Box, Typography } from "@mui/material";
import {
  CheckCircle as DoneIcon,
  ErrorOutline as FailedIcon,
  RadioButtonUnchecked as NotSetIcon,
  RemoveCircleOutline as SkippedIcon,
  Schedule as PendingIcon,
} from "@mui/icons-material";
import { t } from "../../../dashboard/coach/coachTokens";
import StepShell from "../StepShell";
import { STEP_LABELS, type StepId, type StepStatus, type WizardState } from "../wizardReducer";

const OUTCOMES: Record<
  "saved" | "ready" | "skipped" | "failed" | "untouched",
  { label: string; color: string; background: string; icon: typeof DoneIcon }
> = {
  saved: { label: "הוגדר", color: t.greenText, background: t.greenLight, icon: DoneIcon },
  // create mode: chosen, but nothing is written until the coach presses the final button
  ready: { label: "יוגדר בסיום", color: t.greenText, background: t.greenLight, icon: PendingIcon },
  skipped: { label: "דולג", color: t.t2, background: t.bg, icon: SkippedIcon },
  failed: { label: "ההגדרה נכשלה", color: t.redText, background: t.redLight, icon: FailedIcon },
  untouched: { label: "לא הוגדר", color: t.t3, background: t.white, icon: NotSetIcon },
};

const outcomeOf = (status: StepStatus, hasError: boolean): keyof typeof OUTCOMES => {
  if (status === "saved") return "saved";
  if (status === "ready") return "ready";
  if (status === "skipped") return "skipped";
  if (status === "failed" || hasError) return "failed";
  return "untouched";
};

interface SummaryStepProps {
  state: WizardState;
  rows: Array<{ step: StepId; status: StepStatus }>;
  traineeName?: string;
  onJumpTo: (step: StepId) => void;
}

const SummaryStep = ({ state, rows, traineeName, onJumpTo }: SummaryStepProps) => {
  const isCreate = state.mode === "create";
  const pending = rows.filter(row => row.status === "ready").length;
  const configured = rows.filter(row => row.status === "saved").length;

  const name = traineeName || state.steps.identity.values.name.trim();

  return (
    <StepShell
      title={name ? `${name} — סיכום ההגדרות` : "סיכום ההגדרות"}
      description={
        isCreate
          ? `${pending} מתוך ${rows.length} שלבים מוכנים. הלקוח ייווצר וכל ההגדרות יוחלו כשתלחץ על "צור לקוח וסיים" — רק אז יישלח לו מייל ההצטרפות.`
          : `${configured} מתוך ${rows.length} שלבים הוגדרו. מה שלא הוגדר אפשר להשלים בכל רגע מעמוד הלקוח.`
      }
    >
      {/* A real list: the same outcome words appear in the step rail, so anything reading the
          page — a screen reader or a test — needs a way to say "the summary's copy". */}
      <Box
        role="list"
        aria-label="סיכום השלבים"
        sx={{ border: `1px solid ${t.border}`, borderRadius: t.rs, overflow: "hidden" }}
      >
        {rows.map(row => {
          const outcome = OUTCOMES[outcomeOf(row.status, Boolean(state.steps[row.step].error))];
          const Icon = outcome.icon;
          // identity can be revisited while the client does not exist yet
          const jumpable = row.step !== "identity" || isCreate;

          return (
            <Box
              key={row.step}
              role="listitem"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                padding: "12px 14px",
                borderBottom: `1px solid ${t.borderLight}`,
                "&:last-of-type": { borderBottom: "none" },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
                <Icon sx={{ fontSize: 18, color: outcome.color }} />
                <Box sx={{ fontSize: "13.5px", fontWeight: 600, color: t.text }}>
                  {STEP_LABELS[row.step]}
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <Box
                  sx={{
                    fontSize: "11.5px",
                    fontWeight: 600,
                    color: outcome.color,
                    backgroundColor: outcome.background,
                    border: `1px solid ${outcome.color}22`,
                    borderRadius: "999px",
                    padding: "3px 10px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {outcome.label}
                </Box>
                {jumpable && (
                  <Box
                    component="button"
                    type="button"
                    onClick={() => onJumpTo(row.step)}
                    sx={{
                      border: "none",
                      background: "none",
                      padding: 0,
                      cursor: "pointer",
                      fontFamily: "inherit",
                      fontSize: "12px",
                      fontWeight: 600,
                      color: t.green,
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {row.status === "saved" ? "עריכה" : "הגדר"}
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      <Typography sx={{ fontSize: "12px", color: t.t3, marginTop: "16px", lineHeight: 1.6 }}>
        צ'ק-אין חוזר, מדדי גוף ותמונות התקדמות מוגדרים מעמוד הלקוח ומהמסכים הייעודיים.
      </Typography>
    </StepShell>
  );
};

export default SummaryStep;
